import { FilterAlt } from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  Button,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import type {
  GetImprovesRequest,
  PlaceNode,
} from '@ulysses-inc/harami_api_client'
import { ImproveDetailedStatusEnum } from '@ulysses-inc/harami_api_client'
import { ja } from 'date-fns/locale'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import date from 'src/exShared/util/date'
import { useListFilter } from 'src/hooks/filter/useListFilter'
import type { GetImprovesFilter } from 'src/state/ducks/improves/types'
import type { RootState } from 'src/state/store'

// 一覧の通常表示 (getStatusName) と文言を揃え、API の 5値 detailedStatus に対応する。
// 並び順は API の enum 数値昇順 (= 業務フロー順): 指摘入力待ち → 指摘確認中 → 改善確認中 → 差し戻し → 完了
const statusOptions: { value: ImproveDetailedStatusEnum; label: string }[] = [
  {
    value: ImproveDetailedStatusEnum.AuditorAwaitingIndication,
    label: '指摘入力待ち',
  },
  {
    value: ImproveDetailedStatusEnum.PlaceUserAwaitingIndicationReview,
    label: '指摘確認中',
  },
  {
    value: ImproveDetailedStatusEnum.AuditorReviewingRevise,
    label: '改善確認中',
  },
  { value: ImproveDetailedStatusEnum.PlaceUserRemanded, label: '差し戻し' },
  { value: ImproveDetailedStatusEnum.Completed, label: '完了' },
]

interface OwnProps {
  places: PlaceNode[]
  getFilterImproves: (filter?: GetImprovesFilter) => void
  request?: GetImprovesRequest
  isLoadingPlace: boolean
}

const ImprovesFilterDropDown: React.FC<OwnProps> = (props: OwnProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isOpen = Boolean(anchorEl)
  const { filters, setListFilter } = useListFilter()
  const [reportName, setReportName] = useState<string>(
    filters?.improves?.filter?.reportName || '',
  )
  const [placeNodeIds, setPlaceNodeIds] = useState<string[]>(
    filters?.improves?.filter?.placeNodeIds || [],
  )
  const [from, setFrom] = useState<string | undefined>(
    filters?.improves?.filter?.from || undefined,
  )
  const [through, setThrough] = useState<string | undefined>(
    filters?.improves?.filter?.through || undefined,
  )
  const [statuses, setStatuses] = useState<ImproveDetailedStatusEnum[]>(
    (filters?.improves?.filter
      ?.detailedStatus as ImproveDetailedStatusEnum[]) || [],
  )

  const isLoadingPlace = useSelector(
    (state: RootState) => state.placesState.placeGroups.isLoading,
  )

  // 初回及び、全現場表示ボタン切り替え時に絞り込みで選択可能な現場をセット
  useEffect(() => {
    if (props.places.length === 0 || isLoadingPlace) {
      return
    }
    // eslint-disable-next-line react-hooks/immutability -- 関数の宣言順序による既存パターンのため、リファクタリング時に修正する
    const filter = getFilter(reportName, placeNodeIds, from, through, statuses)
    props.getFilterImproves(filter)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.places, isLoadingPlace])

  const isFilterOn = () => {
    return (
      reportName ||
      placeNodeIds.length > 0 ||
      from ||
      through ||
      statuses.length > 0
    )
  }

  const saveFilter = (
    reportName?: string,
    placeNodeIds?: string[],
    from?: string,
    through?: string,
    statuses?: ImproveDetailedStatusEnum[],
  ) => {
    setListFilter({
      ...(filters ?? {}),
      improves: {
        pagination: props.request,
        filter: {
          reportName: reportName ?? '',
          placeNodeIds: placeNodeIds,
          from: from ?? '',
          through: through ?? '',
          detailedStatus: statuses,
        },
      },
    })
  }

  const getFilter = (
    reportName?: string,
    placeNodeIds?: string[],
    from?: string,
    through?: string,
    statuses?: ImproveDetailedStatusEnum[],
  ): GetImprovesFilter => {
    const reportNameCondition =
      reportName && reportName.length > 0 ? { $like: reportName } : undefined
    const placeNodeIdCondition =
      placeNodeIds && placeNodeIds.length > 0
        ? { $in: placeNodeIds }
        : undefined
    // throughは画面表示の日付＋１日する
    const reportDateCondition =
      from && through
        ? {
            $gte: date.parseYYYYMMDD(from),
            $lte: date.nextDay(date.parseYYYYMMDD(through)),
          }
        : undefined
    const detailedStatusCondition =
      statuses && statuses.length > 0 ? statuses : undefined
    saveFilter(reportName, placeNodeIds, from, through, statuses)
    return {
      reportName: reportNameCondition,
      placeNodeId: placeNodeIdCondition,
      reportDate: reportDateCondition,
      detailedStatus: detailedStatusCondition,
    }
  }

  const clearFilter = () => {
    setReportName('')
    setPlaceNodeIds([])
    setFrom(undefined)
    setThrough(undefined)
    setStatuses([])
    const filter = getFilter('', [], undefined, undefined, [])
    props.getFilterImproves(filter)
  }

  const handleReportNameChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newReportName = event.target.value
    setReportName(newReportName)
    const filter = getFilter(
      newReportName,
      placeNodeIds,
      from,
      through,
      statuses,
    )
    props.getFilterImproves(filter)
  }

  const handlePlaceNodeChange = (selectedPlaceNodeIds: string[]) => {
    setPlaceNodeIds(selectedPlaceNodeIds)
    const filter = getFilter(
      reportName,
      selectedPlaceNodeIds,
      from,
      through,
      statuses,
    )
    props.getFilterImproves(filter)
  }

  const handleStartDateChange = (selectedDate: Date | null) => {
    const newFrom = selectedDate
      ? date.formatYYYYMMDD_hyphen(selectedDate)
      : undefined
    setFrom(newFrom)
    const filter = getFilter(
      reportName,
      placeNodeIds,
      newFrom,
      through,
      statuses,
    )
    props.getFilterImproves(filter)
  }

  const handleEndDateChange = (selectedDate: Date | null) => {
    const newThrough = selectedDate
      ? date.formatYYYYMMDD_hyphen(selectedDate)
      : undefined
    setThrough(newThrough)
    const filter = getFilter(
      reportName,
      placeNodeIds,
      from,
      newThrough,
      statuses,
    )
    props.getFilterImproves(filter)
  }

  const handleStatusChange = (
    selectedStatuses: ImproveDetailedStatusEnum[],
  ) => {
    setStatuses(selectedStatuses)
    const filter = getFilter(
      reportName,
      placeNodeIds,
      from,
      through,
      selectedStatuses,
    )
    props.getFilterImproves(filter)
  }

  return (
    <>
      <Button
        color={isFilterOn() ? 'filtered' : 'secondary'}
        variant="outlined"
        startIcon={<FilterAlt />}
        onClick={({ currentTarget }) => setAnchorEl(currentTarget)}
      >
        絞り込み: {isFilterOn() ? 'ON' : 'OFF'}
      </Button>
      <Popover
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: { sx: { width: 400, p: 2 } },
        }}
      >
        {!props.isLoadingPlace && (
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={clearFilter}>クリア</Button>
            </Box>
            <Stack spacing={1}>
              <Typography variant="subtitle1">レポート名</Typography>
              <TextField
                fullWidth
                size="small"
                value={reportName}
                onChange={handleReportNameChange}
                placeholder=""
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <Stack spacing={1}>
              <Typography variant="subtitle1">現場名</Typography>
              <Autocomplete
                multiple
                size="small"
                options={props.places}
                getOptionLabel={place => place.place?.name ?? ''}
                value={props.places.filter(place =>
                  placeNodeIds.includes(place.uuid),
                )}
                isOptionEqualToValue={(option, value) =>
                  option.uuid === value.uuid
                }
                onChange={(_, newValue) =>
                  handlePlaceNodeChange(newValue.map(p => p.uuid))
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder=""
                    InputLabelProps={{ shrink: true }}
                  />
                )}
                slotProps={{
                  chip: {
                    size: 'small',
                    variant: 'pale',
                  },
                }}
              />
            </Stack>
            <Stack spacing={1}>
              <Typography variant="subtitle1">実施日</Typography>
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={ja}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <DatePicker
                    value={from ? date.parseYYYYMMDD(from) : null}
                    onChange={handleStartDateChange}
                    slotProps={{
                      textField: { size: 'small' },
                    }}
                  />
                  <Typography variant="body2">〜</Typography>
                  <DatePicker
                    value={through ? date.parseYYYYMMDD(through) : null}
                    onChange={handleEndDateChange}
                    slotProps={{
                      textField: { size: 'small' },
                    }}
                  />
                </Stack>
              </LocalizationProvider>
            </Stack>
            <Stack spacing={1}>
              <Typography variant="subtitle1">ステータス</Typography>
              <Autocomplete
                multiple
                size="small"
                options={statusOptions}
                getOptionLabel={option => option.label}
                value={statusOptions.filter(opt =>
                  statuses.includes(opt.value),
                )}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                onChange={(_, newValue) =>
                  handleStatusChange(newValue.map(o => o.value))
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder=""
                    InputLabelProps={{ shrink: true }}
                  />
                )}
                slotProps={{
                  chip: {
                    size: 'small',
                    variant: 'pale',
                  },
                }}
              />
            </Stack>
          </Stack>
        )}
      </Popover>
    </>
  )
}

export default ImprovesFilterDropDown
